import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	ConfirmDeleteDialog,
	ConfirmDeleteDialogConfirmation,
	ConfirmDeleteDialogContent,
	ConfirmDeleteDialogFooter,
	ConfirmDeleteDialogHeader,
} from "@/components/ui/confirm-delete-dialog";
import { useSession } from "@/hooks/auth-hooks";
import { CONTACT_EMAIL } from "@/lib/constants";

const MAX_TEAM_MEMBERS = 100;

export const Route = createFileRoute(
	"/dashboard/workspaces/$workspaceSlug/settings",
)({
	component: WorkspaceSettingsPage,
});

interface Member {
	id: string;
	role: string;
	userId: string;
	userName?: string;
	userEmail?: string;
	userImage?: string;
	joinedAt: string;
}

interface Invitation {
	id: string;
	email: string;
	role: string;
	createdAt: string;
	expiresAt: string;
	inviterName?: string;
}

function WorkspaceSettingsPage() {
	const { workspaceSlug } = useParams({
		from: "/dashboard/workspaces/$workspaceSlug/settings",
	});
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useSession();
	const { workspace, workspaces, switchWorkspace, isWorkspacesLoading } =
		useDashboardContext();

	const [inviteEmail, setInviteEmail] = useState("");
	const [inviteLoading, setInviteLoading] = useState(false);
	const [inviteError, setInviteError] = useState<string | null>(null);
	const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

	const [renameName, setRenameName] = useState("");
	const [renameLoading, setRenameLoading] = useState(false);
	const [renameError, setRenameError] = useState<string | null>(null);
	const [renameSuccess, setRenameSuccess] = useState(false);

	const [deleteConfirmName, setDeleteConfirmName] = useState("");
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const [leaveLoading, setLeaveLoading] = useState(false);

	const targetWorkspace = useMemo(
		() => workspaces.find((item) => item.slug === workspaceSlug) ?? null,
		[workspaceSlug, workspaces],
	);

	// Sync active workspace in the background -- don't block rendering
	useEffect(() => {
		if (isWorkspacesLoading || !targetWorkspace) return;
		if (workspace?.id === targetWorkspace.id) return;

		void switchWorkspace(targetWorkspace.id).catch(() => {
			// silent -- workspace data is already available via targetWorkspace
		});
	}, [isWorkspacesLoading, switchWorkspace, targetWorkspace, workspace?.id]);

	useEffect(() => {
		if (!targetWorkspace) return;
		setRenameName(targetWorkspace.name);
		setRenameError(null);
		setRenameSuccess(false);
	}, [targetWorkspace?.id, targetWorkspace?.name]);

	const { data, isLoading } = useQuery({
		queryKey: ["workspace-members"],
		queryFn: async () => {
			const res = await fetch("/api/workspaces/members", {
				credentials: "include",
			});
			if (!res.ok) throw new Error("Failed to load members");
			return res.json() as Promise<{
				members: Member[];
				invitations: Invitation[];
			}>;
		},
		enabled: !!user && !!targetWorkspace,
		refetchInterval: 15_000,
	});

	const members = data?.members ?? [];
	const invitations = data?.invitations ?? [];
	const currentMember = members.find((member) => member.userId === user?.id);
	const isOwner = currentMember?.role === "owner";
	const seatCount = members.length + invitations.length;

	const handleInvite = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!inviteEmail.trim()) return;

			setInviteLoading(true);
			setInviteError(null);
			setInviteSuccess(null);

			try {
				const res = await fetch("/api/workspaces/invitations", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ email: inviteEmail.trim().toLowerCase() }),
				});
				const result = await res.json();

				if (!res.ok) {
					setInviteError(result.error || "Failed to send invitation");
					return;
				}

				setInviteSuccess(`Invitation sent to ${inviteEmail}`);
				setInviteEmail("");
				queryClient.invalidateQueries({ queryKey: ["workspace-members"] });
			} catch {
				setInviteError("Failed to send invitation");
			} finally {
				setInviteLoading(false);
			}
		},
		[inviteEmail, queryClient],
	);

	const handleRemoveMember = useCallback(
		async (memberId: string, name: string) => {
			if (!confirm(`Remove ${name} from this workspace?`)) return;

			try {
				const res = await fetch("/api/workspaces/members", {
					method: "DELETE",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ memberId }),
				});
				const result = await res.json();
				if (!res.ok) {
					alert(result.error || "Failed to remove member");
					return;
				}
				queryClient.invalidateQueries({ queryKey: ["workspace-members"] });
			} catch {
				alert("Failed to remove member");
			}
		},
		[queryClient],
	);

	const handleCancelInvitation = useCallback(
		async (invitationId: string) => {
			try {
				const res = await fetch("/api/workspaces/invitations", {
					method: "DELETE",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ invitationId }),
				});
				const result = await res.json();
				if (!res.ok) {
					alert(result.error || "Failed to cancel invitation");
					return;
				}
				queryClient.invalidateQueries({ queryKey: ["workspace-members"] });
			} catch {
				alert("Failed to cancel invitation");
			}
		},
		[queryClient],
	);

	const handleRename = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!targetWorkspace || !renameName.trim()) return;

			setRenameLoading(true);
			setRenameError(null);
			setRenameSuccess(false);

			try {
				const res = await fetch("/api/workspaces", {
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({
						workspaceId: targetWorkspace.id,
						name: renameName.trim(),
					}),
				});
				const result = await res.json();
				if (!res.ok) {
					setRenameError(result.error || "Failed to rename workspace");
					return;
				}
				setRenameSuccess(true);
				await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
				await queryClient.invalidateQueries({
					queryKey: ["workspace-members"],
				});
				navigate({
					to: "/dashboard/workspaces/$workspaceSlug/settings",
					params: { workspaceSlug: result.workspace.slug },
					replace: true,
				});
			} catch {
				setRenameError("Failed to rename workspace");
			} finally {
				setRenameLoading(false);
			}
		},
		[navigate, queryClient, renameName, targetWorkspace],
	);

	const handleDelete = useCallback(async () => {
		if (!targetWorkspace) return;

		setDeleteLoading(true);
		setDeleteError(null);

		try {
			const res = await fetch("/api/workspaces", {
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ workspaceId: targetWorkspace.id }),
			});
			const result = await res.json();
			if (!res.ok) {
				setDeleteError(result.error || "Failed to delete workspace");
				return;
			}
			await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			setDeleteDialogOpen(false);
			setDeleteConfirmName("");
			navigate({ to: "/dashboard/generations" });
		} catch {
			setDeleteError("Failed to delete workspace");
		} finally {
			setDeleteLoading(false);
		}
	}, [navigate, queryClient, targetWorkspace]);

	const handleLeave = useCallback(async () => {
		if (!confirm("Are you sure you want to leave this workspace?")) return;

		setLeaveLoading(true);
		try {
			const res = await fetch("/api/workspaces/members", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ action: "leave" }),
			});
			const result = await res.json();
			if (!res.ok) {
				alert(result.error || "Failed to leave workspace");
				return;
			}
			await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			navigate({ to: "/dashboard/generations" });
		} catch {
			alert("Failed to leave workspace");
		} finally {
			setLeaveLoading(false);
		}
	}, [navigate, queryClient]);

	if (!targetWorkspace && !isWorkspacesLoading) {
		return (
			<DashboardLayout>
				<div className="mx-auto flex min-h-[50vh] max-w-3xl items-center justify-center">
					<p className="text-sm" style={{ color: "#888888" }}>
						Workspace not found.
					</p>
				</div>
			</DashboardLayout>
		);
	}

	if (!targetWorkspace) {
		return (
			<DashboardLayout>
				<div className="mx-auto flex min-h-[50vh] max-w-3xl items-center justify-center">
					<span
						className="material-symbols-outlined animate-spin" aria-hidden="true"
						style={{ fontSize: "20px", color: "#888888" }}
					>
						progress_activity
					</span>
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			{/* Header */}
				<header className="mb-12">
					<p
						className="text-[11px] font-bold uppercase tracking-[0.1em] mb-2"
						style={{ fontFamily: "inherit", color: "#6B3FF2" }}
					>
						Dashboard
					</p>
					<h1
						className="text-5xl font-semibold tracking-tighter mb-2"
						style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}
					>
						Workspace Settings
					</h1>
					<p
						className="text-xs uppercase tracking-wide font-bold"
						style={{ fontFamily: "inherit", color: "#888888" }}
					>
						{targetWorkspace.name}
					</p>
				</header>

				<div className="max-w-lg space-y-6">
					{/* General */}
					<div
						className="p-6"
						style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
					>
						<h2
							className="text-xl font-semibold tracking-tight"
							style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}
						>
							Workspace name
						</h2>
						<p className="mt-1 text-sm" style={{ color: "#888888" }}>
							Used to identify this workspace across the app.
						</p>

						{isOwner ? (
							<form onSubmit={handleRename} className="mt-6">
								<div className="flex gap-2">
									<input
										id="workspace-name"
										aria-label="Workspace name"
										type="text"
										value={renameName}
										onChange={(event) => {
											setRenameName(event.target.value);
											setRenameError(null);
											setRenameSuccess(false);
										}}
										maxLength={80}
										className="h-9 flex-1 px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/40"
										style={{
											backgroundColor: "#0a0a0a",
											border: "1px solid rgba(255,255,255,0.1)",
											color: "#ffffff",
											fontFamily: "inherit",
										}}
									/>
									<button
										type="submit"
										disabled={
											renameLoading ||
											!renameName.trim() ||
											renameName.trim() === targetWorkspace.name
										}
										className="flex items-center gap-1.5 h-9 px-5 text-xs font-bold uppercase tracking-wide transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
										style={{
											fontFamily: "inherit",
											backgroundColor: "#6B3FF2",
											color: "#ffffff",
										}}
									>
										{renameLoading ? (
											<span className="material-symbols-outlined animate-spin" aria-hidden="true" style={{ fontSize: "14px" }}>progress_activity</span>
										) : null}
										Save
									</button>
								</div>
								{renameError && (
									<p className="mt-2 text-xs" style={{ color: "#ff6e84" }}>{renameError}</p>
								)}
								{renameSuccess && (
									<p className="mt-2 text-xs" style={{ color: "#2b9a66" }}>
										Workspace renamed.
									</p>
								)}
							</form>
						) : (
							<p className="mt-4 text-sm" style={{ color: "#888888" }}>
								Only workspace owners can rename this workspace.
							</p>
						)}
					</div>

					{/* Members */}
					<div
						className="p-6"
						style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
					>
						<div className="flex items-center justify-between">
							<div>
								<h2
									className="text-xl font-semibold tracking-tight"
									style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}
								>
									Members
								</h2>
								<p className="mt-1 text-sm" style={{ color: "#888888" }}>
									{seatCount} of {MAX_TEAM_MEMBERS} seats used
								</p>
							</div>
						</div>

						{isOwner && (
							<form onSubmit={handleInvite} className="mt-6">
								<div className="flex gap-2">
									<input
										type="email"
										aria-label="Email address to invite"
										value={inviteEmail}
										onChange={(event) => {
											setInviteEmail(event.target.value);
											setInviteError(null);
											setInviteSuccess(null);
										}}
										placeholder="email@example.com"
										className="h-9 flex-1 px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/40"
										style={{
											backgroundColor: "#0a0a0a",
											border: "1px solid rgba(255,255,255,0.1)",
											color: "#ffffff",
											fontFamily: "inherit",
										}}
										required
									/>
									<button
										type="submit"
										disabled={inviteLoading || seatCount >= MAX_TEAM_MEMBERS}
										className="flex items-center gap-1.5 h-9 px-5 text-xs font-bold uppercase tracking-wide transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
										style={{
											fontFamily: "inherit",
											backgroundColor: "#6B3FF2",
											color: "#ffffff",
										}}
									>
										{inviteLoading ? (
											<span className="material-symbols-outlined animate-spin" aria-hidden="true" style={{ fontSize: "14px" }}>progress_activity</span>
										) : null}
										Invite
									</button>
								</div>
								{inviteError && (
									<p className="mt-2 text-xs" style={{ color: "#ff6e84" }}>{inviteError}</p>
								)}
								{inviteSuccess && (
									<p className="mt-2 text-xs" style={{ color: "#2b9a66" }}>
										{inviteSuccess}
									</p>
								)}
								{seatCount >= MAX_TEAM_MEMBERS && (
									<p className="mt-2 text-xs" style={{ color: "#888888" }}>
										All seats are in use. Need more? Email{" "}
										<a
											href={`mailto:${CONTACT_EMAIL}`}
											className="transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
											style={{ color: "#6B3FF2" }}
											onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "#ffffff"; }}
											onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "#6B3FF2"; }}
											onFocus={e => { (e.currentTarget as HTMLElement).style.color = "#ffffff"; }}
											onBlur={e => { (e.currentTarget as HTMLElement).style.color = "#6B3FF2"; }}
										>
											{CONTACT_EMAIL}
										</a>
										.
									</p>
								)}
							</form>
						)}

						<div
							className="mt-6 overflow-hidden rounded-none"
							style={{ border: "1px solid rgba(255,255,255,0.08)" }}
						>
							{isLoading ? (
								<div className="flex items-center gap-2 px-4 py-3 text-xs" style={{ color: "#888888" }}>
									<span className="material-symbols-outlined animate-spin" aria-hidden="true" style={{ fontSize: "14px" }}>progress_activity</span>
									Loading…
								</div>
							) : members.length === 0 ? (
								<div className="px-4 py-3 text-sm" style={{ color: "#888888" }}>
									No members found.
								</div>
							) : (
								<ul>
									{members.map((member, idx) => (
										<li
											key={member.id}
											className="flex items-center justify-between gap-3 px-4 py-3"
											style={{ borderTop: idx > 0 ? "1px solid rgba(255,255,255,0.08)" : undefined, backgroundColor: "rgba(255,255,255,0.04)" }}
										>
											<div className="min-w-0">
												<p className="truncate text-sm" style={{ fontFamily: "'Geist', -apple-system, sans-serif", color: "#ffffff" }}>
													{member.userName || member.userEmail}
													{member.userId === user?.id && (
														<span className="ml-1" style={{ color: "#888888" }}>
															(you)
														</span>
													)}
												</p>
												{member.userEmail && member.userName && (
													<p className="text-xs mt-0.5" style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#888888" }}>
														{member.userEmail}
													</p>
												)}
											</div>
											<div className="flex items-center gap-3">
												<span className="text-[10px] uppercase tracking-[0.1em] font-bold" style={{ fontFamily: "inherit", color: "#6B3FF2" }}>
													{member.role}
												</span>
												{isOwner &&
													member.role !== "owner" &&
													member.userId !== user?.id && (
														<button
															type="button"
															onClick={() =>
																handleRemoveMember(
																	member.id,
																	member.userName ||
																		member.userEmail ||
																		"this member",
																)
															}
															className="text-xs transition-colors hover:text-[#ff6e84] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
															style={{ color: "#888888" }}
														>
															Remove
														</button>
													)}
											</div>
										</li>
									))}
								</ul>
							)}
						</div>

						{invitations.length > 0 && (
							<div className="mt-6">
								<p
									className="mb-3 text-[10px] uppercase tracking-[0.1em] font-bold"
									style={{ fontFamily: "inherit", color: "#6B3FF2" }}
								>
									Pending invitations
								</p>
								<div
									className="overflow-hidden rounded-none"
									style={{ border: "1px solid rgba(255,255,255,0.08)" }}
								>
									<ul>
										{invitations.map((invitation, idx) => (
											<li
												key={invitation.id}
												className="flex items-center justify-between gap-3 px-4 py-3"
												style={{
													borderTop: idx > 0 ? "1px solid rgba(255,255,255,0.08)" : undefined,
													backgroundColor: "rgba(255,255,255,0.04)",
												}}
											>
												<div className="min-w-0">
													<p className="truncate text-sm" style={{ fontFamily: "'Geist', -apple-system, sans-serif", color: "#ffffff" }}>
														{invitation.email}
													</p>
													<p className="text-xs mt-0.5" style={{ fontFamily: "inherit", color: "#888888" }}>
														Invited{" "}
														{new Date(invitation.createdAt).toLocaleDateString()}
													</p>
												</div>
												{isOwner && (
													<button
														type="button"
														onClick={() => handleCancelInvitation(invitation.id)}
														className="text-xs transition-colors hover:text-[#ff6e84] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
														style={{ color: "#888888" }}
													>
														Cancel
													</button>
												)}
											</li>
										))}
									</ul>
								</div>
							</div>
						)}
					</div>

					{/* Leave workspace (non-owners) */}
					{!isOwner && currentMember && (
						<div
							className="p-6"
							style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
						>
							<h2
								className="text-xl font-semibold tracking-tight"
								style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								Leave workspace
							</h2>
							<p className="mt-1 text-sm" style={{ color: "#888888" }}>
								You will lose access to this workspace and its content.
							</p>
							<button
								type="button"
								className="mt-6 px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors hover:opacity-80 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{ fontFamily: "inherit", border: "1px solid rgba(255,255,255,0.08)", color: "var(--km-on-surface-muted)" }}
								onClick={handleLeave}
								disabled={leaveLoading}
							>
								{leaveLoading ? "Leaving…" : "Leave workspace"}
							</button>
						</div>
					)}

					{/* Danger zone (owners) */}
					{isOwner && (
						<div
							className="rounded-none p-6"
							style={{ backgroundColor: "rgba(255,110,132,0.04)", border: "1px solid #ff6e84", borderTop: "3px solid #ff6e84" }}
						>
							<h2
								className="text-xl font-semibold tracking-tight"
								style={{ color: "#ff6e84", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								Delete workspace
							</h2>
							<p className="mt-1 text-sm" style={{ color: "#888888" }}>
								Permanently remove this workspace, its members, content, and
								connections.
							</p>
							<button
								type="button"
								className="mt-6 flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6e84]/60"
								style={{
									fontFamily: "inherit",
									backgroundColor: "#ff6e84",
									color: "#2d0a0f",
								}}
								onClick={() => setDeleteDialogOpen(true)}
								disabled={workspaces.length <= 1}
							>
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", fontVariationSettings: "'FILL' 1" }}>delete</span>
								Delete workspace
							</button>
							{workspaces.length <= 1 && (
								<p className="mt-3 text-xs" style={{ color: "#888888" }}>
									You cannot delete your only workspace.
								</p>
							)}
							{deleteError && (
								<p className="mt-2 text-xs" style={{ color: "#ff6e84" }}>{deleteError}</p>
							)}
						</div>
					)}

					<ConfirmDeleteDialog
						open={deleteDialogOpen}
						onOpenChange={(open) => {
							setDeleteDialogOpen(open);
							if (!open) {
								setDeleteConfirmName("");
								setDeleteError(null);
							}
						}}
					>
						<ConfirmDeleteDialogContent>
							<ConfirmDeleteDialogHeader
								title="Delete workspace"
								description="This will permanently delete this workspace, all its members, content, and connections. This action cannot be undone."
							/>
							<ConfirmDeleteDialogConfirmation
								value={deleteConfirmName}
								onChange={setDeleteConfirmName}
								confirmText={targetWorkspace.name}
								showCopyButton
							/>
							{deleteError && (
								<p className="px-1 text-sm" style={{ color: "#ff6e84" }}>{deleteError}</p>
							)}
							<ConfirmDeleteDialogFooter
								onCancel={() => {
									setDeleteDialogOpen(false);
									setDeleteConfirmName("");
									setDeleteError(null);
								}}
								onConfirm={handleDelete}
								confirmDisabled={deleteConfirmName !== targetWorkspace.name}
								isLoading={deleteLoading}
								confirmLabel="Delete workspace"
							/>
						</ConfirmDeleteDialogContent>
					</ConfirmDeleteDialog>
			</div>
		</DashboardLayout>
	);
}
